export type Ga4EventParams = Record<string, unknown>;

let initialized = false;

const getMeasurementId = (): string | undefined => {
  const id = process.env.REACT_APP_GA_MEASUREMENT_ID;
  return id && id.trim() ? id.trim() : undefined;
};

const isProduction = (): boolean => (process.env.NODE_ENV || '').toLowerCase() === 'production';

const isFeatureEnabled = (): boolean => process.env.REACT_APP_ENABLE_GA === 'true';

const sanitizeEventParams = (params?: Ga4EventParams): Ga4EventParams | undefined => {
  if (!params) return undefined;

  const sanitized: Ga4EventParams = {};
  const denyKey = /(email|e-mail|token|password|pass|guid|cpf|cnpj|authorization|bearer)/i;

  for (const [key, value] of Object.entries(params)) {
    if (!key || denyKey.test(key)) continue;

    if (typeof value === 'string') {
      const v = value.trim();
      if (!v) continue;
      if (v.length > 200) continue;
      if (denyKey.test(v)) continue;
      sanitized[key] = v;
      continue;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      sanitized[key] = value;
      continue;
    }

    if (value == null) continue;
  }

  return Object.keys(sanitized).length ? sanitized : undefined;
};

const ensureScript = (measurementId: string): void => {
  const scriptId = 'wg-ga4-gtag';
  if (document.getElementById(scriptId)) return;

  const script = document.createElement('script');
  script.id = scriptId;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
};

export const ga4 = {
  isEnabled: (): boolean => isProduction() && isFeatureEnabled() && !!getMeasurementId(),

  init: (): void => {
    const measurementId = getMeasurementId();
    if (!measurementId) return;
    if (!ga4.isEnabled()) return;
    if (initialized) return;

    initialized = true;
    ensureScript(measurementId);

    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      ((...args: any[]) => {
        window.dataLayer?.push(args);
      });

    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      send_page_view: false,
    });
  },

  pageView: (pagePath: string): void => {
    if (!ga4.isEnabled()) return;
    ga4.init();

    const sanitizedPath = String(pagePath || '').trim();
    if (!sanitizedPath) return;

    window.gtag?.('event', 'page_view', {
      page_path: sanitizedPath,
    });
  },

  event: (name: string, params?: Ga4EventParams): void => {
    if (!ga4.isEnabled()) return;
    ga4.init();

    const eventName = String(name || '').trim();
    if (!eventName) return;

    const sanitizedParams = sanitizeEventParams(params);
    if (sanitizedParams) {
      window.gtag?.('event', eventName, sanitizedParams);
    } else {
      window.gtag?.('event', eventName);
    }
  },
};

